import { FC } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';
import { isEmptyObject } from 'shared/helpers/isEmptyObject';
import { isEmptyObjectValues } from 'shared/helpers/isEmptyObjectValues';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Button } from 'shared/ui/Button';
import { InputField } from 'shared/ui/InputField';
import { PasswordRequirements } from 'shared/ui/PasswordRequirements';
import { ExpiredPasswordRequirements } from 'shared/ui/ExpiredPasswordRequirements';
import { ColorPalette } from 'shared/consts/colorPalette';
import {
	createPasswordSchema,
	changePasswordSchema,
	CreatePasswordValues,
	ChangePasswordValues,
} from './consts/validation';

type CreateProps = {
	mode: 'create';
	onSubmit: (values: CreatePasswordValues) => void;
	isLoading?: boolean;
};

type UpdateProps = {
	mode: 'update' | 'expired';
	onSubmit: (values: ChangePasswordValues) => void;
	isLoading?: boolean;
};

type PasswordFormProps = CreateProps | UpdateProps;

export const PasswordForm: FC<PasswordFormProps> = (props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	if (props.mode === 'create') {
		const initialValues: CreatePasswordValues = { password: '', confirmPassword: '' };

		return (
			<Formik
				initialValues={initialValues}
				validationSchema={createPasswordSchema}
				onSubmit={props.onSubmit}
			>
				{({ errors, values }: FormikProps<CreatePasswordValues>) => (
					<Form>
						<InputField
							name={'password'}
							label={'Password'}
							placeholder={t('auth.validation.enter_password')}
							type={'password'}
							required
						/>
						<InputField
							name={'confirmPassword'}
							label={t('auth.confirm_password')}
							placeholder={t('auth.confirm_password')}
							type={'password'}
							required
						/>
						<PasswordRequirements />
						<Button
							disabled={
								props.isLoading || !isEmptyObject(errors) || isEmptyObjectValues(values)
							}
							type={'submit'}
							fullWidth
						>
							{t('shared_texts.continue')}
						</Button>
					</Form>
				)}
			</Formik>
		);
	}

	const initialValues: ChangePasswordValues = { currentPassword: '', password: '', confirmPassword: '' };

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			validationSchema={changePasswordSchema}
			onSubmit={props.onSubmit}
		>
			{({ errors, values, resetForm }: FormikProps<ChangePasswordValues>) => (
				<Form>
					<InputField
						name={'currentPassword'}
						label={t('personal_account.tab_3.labels.current_password')}
						placeholder={t('personal_account.tab_3.placeholder.current_password')}
						type={'password'}
						required
					/>
					<InputField
						name={'password'}
						label={t('personal_account.tab_3.labels.new_password')}
						placeholder={t('personal_account.tab_3.placeholder.new_password')}
						type={'password'}
						required
					/>
					<InputField
						name={'confirmPassword'}
						label={t('personal_account.tab_3.labels.confirm_new_password')}
						placeholder={t('personal_account.tab_3.placeholder.confirm_new_password')}
						type={'password'}
						required
					/>
					{props.mode === 'expired' ? <ExpiredPasswordRequirements /> : <PasswordRequirements />}
					<ActionsForDialog
						cancelLabel={t('shared_texts.cancel')}
						cancelAction={() => resetForm()}
						applyLabel={t('shared_texts.save')}
						applyActionDisabled={
							props.isLoading || !isEmptyObject(errors) || isEmptyObjectValues(values)
						}
					/>
					<Typography
						variant={'body2'}
						color={ColorPalette.royalBlue}
						onClick={() => navigate('/forgot-password', { state: { access: true } })}
						sx={{
							width: 'fit-content',
							cursor: 'pointer',
							marginTop: '30px',
							marginLeft: 'auto',
						}}
					>
						{t('auth.forgot_password')}
					</Typography>
				</Form>
			)}
		</Formik>
	);
};
